import { Injectable } from '@angular/core';
import type {
  BusinessCardBackground,
  BusinessCardBackgroundStyleId,
  StickerSizeInches
} from '../app.types';

@Injectable({ providedIn: 'root' })
export class BusinessCardPrintService {
  businessCardBackgroundCss(background: BusinessCardBackground): Record<string, string> {
    return {
      backgroundColor: '#ffffff',
      backgroundImage: this.businessCardBackgroundImageValue(background),
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat'
    };
  }

  businessCardPrintDocument(
    title: string,
    sheetHtml: string
  ): string {
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${this.escapeHtml(title)}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    html, body { margin: 0; background: #ffffff; }
    body { width: 7.5in; height: 10in; color: #111827; font-family: Arial, Helvetica, sans-serif; }
    .print-page {
      width: 7.5in;
      min-height: 10in;
      break-after: page;
      page-break-after: always;
    }
    .print-page:last-child {
      break-after: auto;
      page-break-after: auto;
    }
    .business-card-print-page {
      display: grid;
      grid-template-columns: repeat(2, 3.5in);
      grid-template-rows: repeat(5, 2in);
      gap: 0 0.25in;
      width: 7.25in;
      height: 10in;
      max-height: 10in;
      margin-inline: auto;
      align-content: start;
      overflow: hidden;
      break-after: page;
      page-break-after: always;
    }
    .business-card-print-page:last-child {
      break-after: auto;
      page-break-after: auto;
    }
    app-business-card-renderer {
      display: block;
      width: 3.5in;
      height: 2in;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .business-card {
      --card-accent: #164c63;
      --card-foreground: #f9fafb;
      --card-muted: #e5e7eb;
      --business-card-text-small: 5.53pt;
      --business-card-text-normal: 6.5pt;
      --business-card-text-large: 7.8pt;
      --business-card-text-xlarge: 9.43pt;
      --business-card-edge-offset: 0.023in;
      position: relative;
      container-type: inline-size;
      width: 3.5in;
      height: 2in;
      overflow: hidden;
      color: var(--card-foreground);
      border: 0.5pt solid #d1d5db;
      border-radius: 0.045in;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    .business-card__item { position: absolute; }
    .business-card__text-section {
      width: 62%;
      max-width: calc(100% - 0.08in);
      min-height: 0.16in;
      color: var(--card-foreground);
      font-size: var(--business-card-text-normal);
      line-height: 1.35;
      overflow-wrap: anywhere;
      overflow: hidden;
    }
    .business-card__text-section h1 {
      margin: 0 0 0.05em;
      font-size: 1.45em;
      line-height: 1.05;
    }
    .business-card__text-section div,
    .business-card__text-section p,
    .business-card__text-section blockquote,
    .business-card__text-section ul,
    .business-card__text-section ol {
      margin: 0;
    }
    .business-card__qr img {
      width: 100%;
      height: auto;
      aspect-ratio: 1;
      object-fit: contain;
      display: block;
      border: 0.5pt solid #e5e7eb;
      border-radius: 0.045in;
    }
    .business-card__logo img {
      width: 0.63in;
      height: 0.63in;
      object-fit: contain;
      display: block;
      background: #ffffff;
      border: 0.5pt solid #e5e7eb;
      border-radius: 0.045in;
    }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  ${sheetHtml}
  ${this.printAndCloseScript()}
</body>
</html>`;
  }

  stickerSheetPrintDocument(
    title: string,
    stickerHtml: string | string[],
    size: StickerSizeInches
  ): string {
    const layout = this.stickerSheetLayout(size);
    const stickerHtmlItems = Array.isArray(stickerHtml) ? stickerHtml : [stickerHtml];
    const shouldRepeatStickers = !Array.isArray(stickerHtml);
    const stickers = Array.from({ length: layout.count }, (_, index) => (
      shouldRepeatStickers ? stickerHtmlItems[index % stickerHtmlItems.length] : stickerHtmlItems[index] || ''
    )).join('');

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${this.escapeHtml(title)}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    html, body { margin: 0; background: #ffffff; }
    body { width: 7.5in; min-height: 10in; color: #111827; font-family: Arial, Helvetica, sans-serif; }
    .sticker-sheet {
      display: grid;
      grid-template-columns: repeat(${layout.columns}, ${size}in);
      grid-auto-rows: ${size}in;
      width: ${layout.columns * size}in;
      max-width: ${layout.columns * size}in;
      align-content: start;
      justify-content: start;
      overflow: hidden;
    }
    .sticker-sheet > * {
      min-width: 0;
      min-height: 0;
      max-width: ${size}in;
      max-height: ${size}in;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .circle-sticker {
      display: block;
      flex: 0 0 ${size}in;
      width: ${size}in;
      min-width: ${size}in;
      max-width: ${size}in;
      height: ${size}in;
      min-height: ${size}in;
      max-height: ${size}in;
      overflow: hidden;
      border: 0.35pt solid #d1d5db;
      border-radius: 50%;
      line-height: 0;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    .qr-sticker-image {
      display: block;
      width: 100%;
      max-width: 100%;
      height: 100%;
      max-height: 100%;
      object-fit: cover;
    }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <main class="sticker-sheet" aria-label="${this.escapeAttribute(title)}">${stickers}</main>
  ${this.printAndCloseScript()}
</body>
</html>`;
  }

  qrStickerHtml(imageUrl: string, size: StickerSizeInches): string {
    const sizeInches = this.normalizedStickerSize(size);
    const fixedSizeStyle = `width: ${sizeInches}in; height: ${sizeInches}in; max-width: ${sizeInches}in; max-height: ${sizeInches}in;`;
    return `<div class="circle-sticker" style="${fixedSizeStyle}"><img class="qr-sticker-image" src="${this.escapeAttribute(imageUrl)}" alt="" width="720" height="720" style="${fixedSizeStyle}"></div>`;
  }

  private printAndCloseScript(): string {
    return `<script>
    let printStarted = false;
    let closeFallbackTimer;

    const closePrintWindow = () => {
      clearTimeout(closeFallbackTimer);
      setTimeout(() => window.close(), 100);
    };

    const printSheet = () => {
      printStarted = true;
      window.focus();
      window.print();
      closeFallbackTimer = setTimeout(closePrintWindow, 1000);
    };

    window.addEventListener('afterprint', () => {
      if (printStarted) {
        closePrintWindow();
      }
    });

    if (document.readyState === 'complete') {
      setTimeout(printSheet, 100);
    } else {
      window.addEventListener('load', () => setTimeout(printSheet, 100), { once: true });
    }
  </script>`;
  }

  private stickerSheetLayout(size: StickerSizeInches): { columns: number; rows: number; count: number } {
    const columns = Math.max(1, Math.floor(7.5 / size));
    const rows = Math.max(1, Math.floor(10 / size));
    return { columns, rows, count: columns * rows };
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }

  private clampBackgroundFade(value: number): number {
    if (!Number.isFinite(value)) {
      return 85;
    }

    return Math.min(100, Math.max(0, Math.round(value)));
  }

  private normalizedStickerSize(value: number | string): StickerSizeInches {
    if (value === '') {
      return 1;
    }

    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 1;
    }

    return Math.min(2, Math.max(0.5, Math.round(size * 100) / 100));
  }

  private normalizeHexColor(value: unknown, fallback = '#164c63'): string {
    return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
  }

  private businessCardBackgroundImageValue(background: BusinessCardBackground): string {
    const fade = this.clampBackgroundFade(background.fade) / 100;
    const overlay = this.businessCardBackgroundOverlay(background.color, fade, background.style);

    if (!background.imageDataUrl) {
      return overlay;
    }

    return `${overlay}, url("${this.escapeCssUrl(background.imageDataUrl)}")`;
  }

  private businessCardBackgroundOverlay(color: string, fade: number, style: BusinessCardBackgroundStyleId): string {
    const strong = this.hexToRgba(color, fade);
    const medium = this.hexToRgba(color, fade * 0.68);
    const soft = this.hexToRgba(color, fade * 0.18);
    const clear = this.hexToRgba(color, fade * 0.04);
    const hot = this.hexToRgba(this.shiftHexColor(color, 60, 18, 90), Math.min(1, fade * 0.86));
    const cool = this.hexToRgba(this.shiftHexColor(color, -28, 46, 66), Math.min(1, fade * 0.78));
    const dark = this.hexToRgba(this.shiftHexColor(color, -76, -64, -46), Math.min(1, fade * 0.88));
    const light = this.hexToRgba(this.shiftHexColor(color, 86, 78, 54), Math.min(1, fade * 0.7));

    switch (style) {
      case 'aurora':
        return [
          `radial-gradient(70% 82% at 58% 44%, ${hot} 0%, ${medium} 25%, ${clear} 58%)`,
          `radial-gradient(72% 80% at 18% 18%, ${cool} 0%, ${soft} 54%, transparent 78%)`,
          `linear-gradient(145deg, ${dark} 0%, ${strong} 46%, ${soft} 100%)`
        ].join(', ');
      case 'storm':
        return [
          `radial-gradient(74% 64% at 62% 0%, ${light} 0%, ${soft} 36%, transparent 72%)`,
          `radial-gradient(82% 74% at 42% 46%, ${cool} 0%, ${medium} 42%, transparent 76%)`,
          `linear-gradient(150deg, ${dark} 0%, ${strong} 58%, ${this.hexToRgba('#020617', Math.min(1, fade * 0.76))} 100%)`
        ].join(', ');
      case 'sunrise':
        return [
          `radial-gradient(72% 80% at 80% 18%, ${this.hexToRgba('#fef3c7', Math.min(1, fade * 0.92))} 0%, ${light} 35%, transparent 70%)`,
          `radial-gradient(68% 70% at 10% 58%, ${hot} 0%, ${medium} 42%, transparent 74%)`,
          `linear-gradient(135deg, ${strong} 0%, ${soft} 54%, ${cool} 100%)`
        ].join(', ');
      case 'spotlight':
        return [
          `radial-gradient(34% 48% at 52% 44%, ${hot} 0%, ${medium} 32%, transparent 68%)`,
          `radial-gradient(82% 60% at 46% 52%, ${cool} 0%, ${soft} 38%, transparent 76%)`,
          `linear-gradient(160deg, ${dark} 0%, ${strong} 54%, ${soft} 100%)`
        ].join(', ');
      default:
        return `linear-gradient(135deg, ${strong} 0%, ${medium} 52%, ${soft} 100%)`;
    }
  }

  private businessCardPrintBackgroundStyle(background: BusinessCardBackground): string {
    const foregroundColor = this.readableTextColorForHex(background.color);

    return [
      `--card-accent: ${this.escapeHtml(background.color)}`,
      `--card-foreground: ${foregroundColor}`,
      `--card-muted: ${foregroundColor}`,
      'background-color: #ffffff',
      `background-image: ${this.businessCardBackgroundImageValue(background)}`,
      'background-position: center',
      'background-size: cover',
      'background-repeat: no-repeat'
    ].join('; ');
  }

  private hexToRgba(hex: string, alpha: number): string {
    const color = this.normalizeHexColor(hex).replace('#', '');
    const red = parseInt(color.slice(0, 2), 16);
    const green = parseInt(color.slice(2, 4), 16);
    const blue = parseInt(color.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${Math.min(1, Math.max(0, Math.round(alpha * 100) / 100))})`;
  }

  private readableTextColorForHex(value: string): '#111827' | '#f9fafb' {
    const color = this.normalizeHexColor(value);
    const red = parseInt(color.slice(1, 3), 16);
    const green = parseInt(color.slice(3, 5), 16);
    const blue = parseInt(color.slice(5, 7), 16);
    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

    return luminance > 0.58 ? '#111827' : '#f9fafb';
  }

  private shiftHexColor(hex: string, redShift: number, greenShift: number, blueShift: number): string {
    const color = this.normalizeHexColor(hex).replace('#', '');
    const red = this.clampColorChannel(parseInt(color.slice(0, 2), 16) + redShift);
    const green = this.clampColorChannel(parseInt(color.slice(2, 4), 16) + greenShift);
    const blue = this.clampColorChannel(parseInt(color.slice(4, 6), 16) + blueShift);

    return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
  }

  private clampColorChannel(value: number): number {
    return Math.min(255, Math.max(0, Math.round(value)));
  }

  private escapeCssUrl(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '');
  }
}
